import { useCallback, useRef } from 'react';
import { Range, Editor } from '@tiptap/core';
import { useActiveTiptapEditorCallback } from '../../store/tiptapEditorStoreHooks';
import { parseInlineBoardLinkText } from '../../../../../common/tiptap/extensions/inlineBoardLink/inlineBoardLinkUtils';
import getClientConfig from '../../../../utils/getClientConfig';

const { urlBase } = getClientConfig();

export type BoardLinkSelectItem = {
    boardId: string;
    title: string;
    color: string;
    permissionId?: string;
    url: string;
};

export type UseBoardLinkManagerResult = {
    prepareBoardLinkSelection: () => void;
    handleBoardSelect: (item: BoardLinkSelectItem) => boolean;
    handlePastedBoardLink: (text: string) => boolean;
};

/**
 * Shares board-link insertion state across popup and sheet entry points
 */
export const useBoardLinkManager = (): UseBoardLinkManagerResult => {
    const savedRangeRef = useRef<Range | null>(null);
    const savedEditorRef = useRef<Editor | null>(null);

    /**
     * Saves the editor and selection range before the search UI steals focus
     */
    const prepareBoardLinkSelection = useActiveTiptapEditorCallback((editor) => {
        if (!editor) return;

        const { from, to } = editor.state.selection;
        savedRangeRef.current = { from, to };
        savedEditorRef.current = editor;
    }, []);

    /**
     * Restores the saved state and inserts the inline board link node
     */
    const handleBoardSelect = useCallback((item: BoardLinkSelectItem) => {
        const editor = savedEditorRef.current;
        const range = savedRangeRef.current;

        savedRangeRef.current = null;
        savedEditorRef.current = null;

        if (!editor || !range) return false;

        return editor.commands.createInlineBoardLink(range, item);
    }, []);

    const handlePastedBoardLink = useCallback(
        (text: string) => {
            const parsedBoardLink = parseInlineBoardLinkText(text, urlBase);
            if (!parsedBoardLink) return false;

            return handleBoardSelect({
                boardId: parsedBoardLink.boardId,
                title: parsedBoardLink.title,
                color: '',
                permissionId: parsedBoardLink.permissionId,
                url: parsedBoardLink.url,
            });
        },
        [handleBoardSelect],
    );

    return {
        prepareBoardLinkSelection,
        handleBoardSelect,
        handlePastedBoardLink,
    };
};
